import { useContext } from 'react'
import capitalize from 'lodash/capitalize'
import { Badge } from '@/components/ui/badge'
import Card from 'app/frontend/base/components/Card'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import CandidateName from './CandidateName'
import CandidateCommentAction from './CandidateCommentAction'
import CandidateTeamSelect from './CandidateTeamSelect'
import CandidateContextMenu from './CandidateContextMenu'
import CandidateUserBadge from './CandidateUserBadge'
import { CandidateSetupContext } from './CandidateSetup'
import { useIsCandidateAdmin, useIsOwnCandidate } from '../hooks'

type Props = {
  role: CharacterRole
  className?: string
}

const CandidateRow = ({
  candidate,
}: {
  candidate: wowaudit.GuildCandidate
}) => {
  const isAdmin = useIsCandidateAdmin()
  const isOwn = useIsOwnCandidate(candidate)

  return (
    <Card className='inline-flex items-center justify-between py-1 px-2 gap-1'>
      <CandidateName candidate={candidate} />

      <div className='flex items-center justify-end gap-0.5 shrink-0'>
        <CandidateUserBadge
          candidate={candidate}
          className='border rounded-lg mr-0.5'
        />
        <CandidateCommentAction candidate={candidate} />
        {isAdmin && <CandidateTeamSelect candidate={candidate} size='xs' />}
        {(isAdmin || isOwn) && <CandidateContextMenu candidate={candidate} />}
      </div>
    </Card>
  )
}

const CandidateRoleBlock = ({ role, className }: Props) => {
  const { candidatesByRole } = useContext(CandidateSetupContext)
  const candidates = candidatesByRole[role] ?? []

  return (
    <Card className={className}>
      <h3 className='flex items-center gap-2 justify-between border-b pb-2 mb-2'>
        <div className='flex items-center gap-3'>
          <CharacterRoleIcon role={role} size='large' />
          {capitalize(role)}
        </div>

        <Badge variant='outline' className='text-base'>
          {candidates.length}
        </Badge>
      </h3>

      <div className='flex flex-col gap-1'>
        {candidates.length === 0 ? (
          <p className='italic text-muted-foreground'>
            No candidates with this role
          </p>
        ) : (
          candidates.map((candidate) => (
            <CandidateRow
              key={candidate.characterReference.id}
              candidate={candidate}
            />
          ))
        )}
      </div>
    </Card>
  )
}

export default CandidateRoleBlock
